import type { Translations } from '@bringg/react-components/dist/components/mapbox/mapbox-with-service-areas/mapbox-with-service-areas';
import type { TaskStatusTranslations } from '@bringg/react-components/dist/translations';
import { mapValues as _mapValues } from 'lodash';
import { OptimizationErrors } from '@bringg/types';

export interface RoutesPlannerTranslations {
	driversTab: string;
	runsTab: string;
	vehiclesTab: string;
	breakSymbol: string;
	timeWindowTranslations: TimeWindowTranslations;
	notificationsTranslations: NotificationsTranslation;
	tooltipTranslations: TaskTooltipTranslations;
	multiselectTranslations: MultiselectTranslations;
	serviceAreasTranslations: Translations;
	routesTableTranslations: RoutesTableTranslations;
	actionsTranslations: ActionsTranslations;
	errorMessages: Record<string, string>;
	consecutiveRoutesTranslations: { consecutiveRoutes: string; consecutiveRoute: string };
	dynamicAggregationsTranslations: DynamicAggregationsTranslations;
	kpisTranslations: KPIsTranslations;
}

export type TimeWindowTranslations = {
	noEarlierThan: string;
	noLaterThan: string;
};

export type NotificationsTranslation = {
	outOfDeliveryBlockRange: string;
	outOfDeliveryBlockRangeNotice?: string;
	itemDroppedBeforeCurrentTime: string;
	collectionAfterDeliveryError: string;
	deliveryBeforeCollectionError: string;
	moveLinkedOrder: string;
	unassignedLinkedTasks: string;
	assignedLinkedTasks: string;
	order: string;
	orders: string;
	outsidePromisedWindow: string;
	routeNotMatchSkills: string;
	recommendChangeDriver: string;
};

export type MultiselectTranslations = {
	missingSkillsError: string;
	movedSuccessfully: string;
	unassignSuccessfully: string;
	dontSelectFromOtherRoute: string;
	failedToMove: string;
	moveToStart: string;
	moveToEnd: string;
	moveTo: string;
	unassign: string;
	selected: string;
};

export type TaskTooltipTranslations = {
	deliver: string;
	collect: string;
	moveTask: string;
	taskInfo: string;
	scheduledFor: string;
	totalVolume: string;
	unassign: string;
	taskStatus: TaskStatusTranslations;
	resequenceTransltions: ResequenceTransltions;
	openOrderPage: string;
	driverName: string;
	vehicleName: string;
	routeName: string;
	home: (name: string) => string;
	teamsHome: string;
};

export type TableGroupHeadersTranslations = {
	unassignedOrders: string;
	pickup: string;
	dropoff: string;
	both: string;
	pickupTooltip: string;
	dropoffTooltip: string;
	bothTooltip: string;
	unassignedKey: string;
};

export type EmptyMessageTranslations = {
	contactSupport: string;
	noResults: string;
	getInTouch: string;
};

export type RoutesTableTranslations = {
	header: string;
	customerID: string;
	noCustomerName: string;
	actions: TableActionsTranslations;
	headers: TableHeadersTranslation;
	summaryDetails: SummaryDetailsTranslation;
	groupHeaders: TableGroupHeadersTranslations;
	emptyTable: EmptyMessageTranslations;
	yes: string;
	no: string;
};

export type TableHeadersTranslation = {
	orderId: string;
	customerName: string;
	timeWindow: string;
	eta: string;
	totalWeight: string;
	weight: string;
	skills: string;
	unassigned: string;
	handlingUnits: string;
	miles: string;
	km: string;
	totalDistance: string;
	routeId: string;
	priority: string;
	driverName: string;
	vehicleName: string;
	trailerName: string;
	tasksCount: string;
	plannedStartEndTimes: string;
	unassignedDefaultReason: string;
	routesPlanner: string;
	etos: string;
	rank: string;
	totalPrice: string;
	sla: string;
	orderType: string;
	duration: string;
	runTimes: string;
	driverRank: string;
	reason: string;
};

export type TableActionsTranslations = {
	selectAll: string;
	collapseAll: string;
	expandAll: string;
	moveUnassignToTimeline: string;
	moveUnassignToTimelineIsLocked: string;
	shrinkTable: string;
	expandTable: string;
};

export type SummaryDetailsTranslation = {
	totalOrders: string;
	unassigned: string;
	assigned: string;
	drivers: string;
	routes: string;
	vehicles: string;
	columnsSelectionTitle: string;
	columnsSelectionEmpty: string;
	columnsSelectionDefault: string;
	columnsSelectionSelected: string;
	columnsSelectionUnSelected: string;
	columnsSelectionSearchPlaceholder: string;
};

export type ActionsTranslations = {
	copy: string;
	optimizationUuid: string;
	undoLastChange: string;
};

export type ResequenceTransltions = {
	priority: string;
	route: string;
	apply: string;
};

export type DynamicAggregationsTranslations = {
	currentLoad: string;
	expectedPickup: string;
};

export type RouteRowTranslations = TableHeadersTranslation & TableActionsTranslations & DynamicAggregationsTranslations;

export type KPIsTranslations = {
	kpisByOriginal: string;
	kpisByManual: string;
	assigned: string;
	unassigned: string;
	timeFairness: string;
	routesDuration: string;
	routesDistance: string;
	stopsPerHour: string;
	intersections: string;
};

export const DEFAULT_TRANSLATIONS: RoutesPlannerTranslations = {
	driversTab: 'Drivers',
	vehiclesTab: 'Vehicles',
	errorMessages: {
		generalError: 'Times you see here may not be accurate. Please contact Bringg Support.',
		..._mapValues(OptimizationErrors, value => value.message)
	},
	runsTab: 'Runs',
	breakSymbol: 'BRK',
	timeWindowTranslations: {
		noEarlierThan: 'No Earlier Than',
		noLaterThan: 'No Later Than'
	},
	multiselectTranslations: {
		moveToStart: 'Move to start',
		unassignSuccessfully: 'unassign successfully',
		dontSelectFromOtherRoute: 'dont select from other route',
		moveToEnd: 'Move to end',
		moveTo: 'Move to',
		missingSkillsError: 'Missing skills',
		failedToMove: 'Failed to move',
		movedSuccessfully: 'Moved Successfully',
		unassign: 'Unassign',
		selected: 'Selected'
	},
	notificationsTranslations: {
		outOfDeliveryBlockRange: 'Item Out Of Delivery Block Range',
		outOfDeliveryBlockRangeNotice: 'Note That Item Is Out Of Delivery Block Range',
		itemDroppedBeforeCurrentTime: 'An order cannot be planned prior to current time',
		collectionAfterDeliveryError: 'Collection order cannot be positioned later than its linked Delivery order',
		deliveryBeforeCollectionError: 'Delivery order cannot be positioned before its linked Collection order',
		moveLinkedOrder:
			'Bringg moved another destination linked to this order to the adjacent position in the new route.',
		unassignedLinkedTasks: 'The requested order and its linked destination were unassigned',
		assignedLinkedTasks:
			'Bringg assigned another destination linked to this order to the adjacent position in the new route.',
		order: 'order',
		orders: 'orders',
		outsidePromisedWindow: 'is planned outside of its promised time window',
		routeNotMatchSkills: 'This route has a driver/vehicle which does not match the skills to fulfil this order',
		recommendChangeDriver:
			'We recommend you change to the driver/vehicle with the correct skills. Do you want to proceed'
	},
	tooltipTranslations: {
		deliver: 'Deliver',
		driverName: 'Driver',
		vehicleName: 'Vehicle',
		routeName: 'Route',
		collect: 'Collect',
		scheduledFor: 'Scheduled For',
		totalVolume: 'Total Volume',
		unassign: 'Unassign',
		moveTask: 'Move Order',
		taskInfo: 'Order Info',
		home: name => name + "'s Home",
		teamsHome: "Team's Home",
		taskStatus: {
			notAssigned: 'Not assigned',
			assigned: 'Assigned',
			onTheWay: 'On the way',
			checkedIn: 'Checked in',
			done: 'Done',
			late: 'Late',
			accepted: 'Accepted',
			cancelled: 'Cancelled',
			rejected: 'Rejected',
			unacknowledged: 'Unacknowledged',
			pending: 'Pending'
		},
		resequenceTransltions: {
			priority: 'Priority',
			route: 'Route',
			apply: 'Apply'
		},
		openOrderPage: 'Open order page'
	},
	serviceAreasTranslations: {
		cancel: 'Cancel',
		save: 'Save',
		areaName: 'Area Name',
		deleteAreaTitle: "Are you sure you'd like to delete this area?",
		delete: 'Delete'
	},
	routesTableTranslations: {
		header: 'Optimization Results',
		customerID: 'Customer ID',
		noCustomerName: 'Deleted',
		actions: {
			selectAll: 'Select All',
			collapseAll: 'Collapse All',
			expandAll: 'Expand All',
			moveUnassignToTimeline: 'Move unassign to timeline',
			moveUnassignToTimelineIsLocked: 'Unassigned route cannot be selected',
			shrinkTable: 'Shrink',
			expandTable: 'Expand'
		},
		headers: {
			orderId: 'Order ID',
			customerName: 'Customer Name',
			timeWindow: 'Time Window',
			eta: 'ETA',
			totalWeight: 'Total Weight',
			weight: 'Weight',
			skills: 'Skills',
			unassigned: 'Unassigned',
			handlingUnits: 'Handling Units',
			miles: 'Miles',
			km: 'Km',
			totalDistance: 'Total Distance',
			driverName: 'Driver Name',
			routeId: 'Route ID',
			priority: 'Priority',
			tasksCount: 'Tasks Count',
			plannedStartEndTimes: 'Planned start-end times',
			unassignedDefaultReason: 'Unassigned default reason',
			vehicleName: 'Vehicle name',
			trailerName: 'Trailer name',
			routesPlanner: 'Routes Planner',
			etos: 'ETOS',
			totalPrice: 'Total price',
			rank: 'Rank',
			sla: 'SLA',
			orderType: 'order type',
			duration: 'duration',
			runTimes: 'Run times',
			driverRank: 'Driver Rank',
			reason: 'Reason'
		},
		summaryDetails: {
			totalOrders: 'Total Orders',
			unassigned: 'Unassigned',
			assigned: 'Assigned',
			drivers: 'Drivers',
			routes: 'Routes',
			vehicles: 'Vehicles',
			columnsSelectionTitle: 'table customization',
			columnsSelectionEmpty: 'there are no columns here',
			columnsSelectionDefault: 'restore to default',
			columnsSelectionSelected: 'selected',
			columnsSelectionUnSelected: 'unselected',
			columnsSelectionSearchPlaceholder: 'search'
		},
		groupHeaders: {
			unassignedOrders: 'Unassigned Orders',
			pickup: 'PU',
			dropoff: 'DO',
			both: 'BO',
			pickupTooltip: 'pickup task',
			dropoffTooltip: 'dropoff task',
			bothTooltip: 'pickup dropoff task',
			unassignedKey: 'U'
		},
		emptyTable: {
			contactSupport: 'contact support',
			noResults: 'We cannot display these orders. Make sure you selected only delivery or pickup orders',
			getInTouch: 'If your selection was correct but the issue persists, contact support'
		},
		yes: 'yes',
		no: 'no'
	},
	actionsTranslations: {
		copy: 'Copy to Clipboard',
		optimizationUuid: 'Optimization ID',
		undoLastChange: 'Undo Last Change'
	},
	consecutiveRoutesTranslations: {
		consecutiveRoutes: 'Consecutive routes',
		consecutiveRoute: 'Consecutive route'
	},
	dynamicAggregationsTranslations: {
		currentLoad: 'Loaded',
		expectedPickup: 'To be Loaded'
	},
	kpisTranslations: {
		kpisByOriginal: 'Original UUID',
		kpisByManual: 'Origin',
		assigned: 'Assigned',
		unassigned: 'Unassigned',
		timeFairness: 'Time fairness',
		routesDuration: 'Driver avg tasks',
		routesDistance: 'Driver avg time',
		stopsPerHour: 'Stops per hour',
		intersections: 'Intersections'
	}
};
